/**
 * people/fields.tsx — the `<dl>` row helper both public-profile pages use (`UR-F04`).
 *
 * ── WHY THIS FILE EXISTS ───────────────────────────────────────────────────────────────────────
 *
 * `UR-F04` is a Must: "where a directory item is EMPTY, leave it empty and HIDE it rather than
 * prompting the person to supply it. Blank is an accurate answer." The helper the profile pages
 * shared did the opposite — it rendered a visible "Not provided by the directory" note for every
 * null — so a sparse account showed three or four rows of it. §15 of `REQUIREMENTS_USER_RECORD.md`
 * is explicit that the non-production accounts carry sparse directory data and that `UR-F04` is what
 * makes that "acceptable rather than embarrassing", so the old behaviour broke the rule on most
 * profiles rather than in an edge case.
 *
 * `UR-F11`/`PR-F23` then make it a correctness rule rather than a cosmetic one: "where a department
 * has no support champion, show NOTHING rather than a placeholder". The moment the champion contact
 * is rendered through a row helper, a helper that prints a placeholder for an absent value breaks
 * that requirement by default. The fix belongs in the helper, not in each caller remembering to
 * guard.
 *
 * So `Field` here renders NOTHING AT ALL when the value is empty — no `<dt>`, no `<dd>`, no note.
 * That is the same pattern the sibling `Pills` helper already followed (it returns null on an empty
 * list), so the two now agree and a `<dl>` simply carries fewer rows on a sparse profile.
 *
 * WHITESPACE COUNTS AS EMPTY. A directory field holding `" "` is not an answer, and rendering a blank
 * `<dd>` would leave the visible hole `UR-F04` says to close.
 *
 * NOT A GENERIC UI KIT. This is scoped to the THREE profile surfaces and nothing else: the two public
 * pages (`UserProfile.tsx`, `people/PersonProfile.tsx`) and the owner's own `Profile.tsx`. That last
 * one used to export a rival copy of `Field` printing the placeholder, which is what `UR-F04` named as
 * the offender — it now imports this one, so there is a single answer to "what does an empty directory
 * field look like" rather than one per page. A FOURTH page wanting profile rows is a sign the pages
 * should have been one component (`PR-F03`), not a reason to widen this module.
 *
 * ── ALSO HERE: THE COMMUNITY LABELS (`UR-F34`/`UR-F36`, `PR-F50`) ───────────────────────────────
 *
 * `CommunityLabels` renders `Team` and `RoleLabel` as a group that is visibly subordinate to the
 * directory rows, which is a requirement in its own words and not a styling preference — see its header.
 * It lives beside `Field` because it is the same decision one level up: `Field` answers "what does an
 * empty row look like", this answers "what does a row from a DIFFERENT SOURCE look like", and both public
 * profiles have to answer them identically or a reader learns the rule from one page and is misled by
 * the other.
 */

/**
 * One `<dt>`/`<dd>` pair, or NOTHING when there is no value (`UR-F04`, `UR-F11`/`PR-F23`).
 *
 * Must be rendered inside a `<dl>` — it returns a fragment of two siblings so the grid layout the
 * pages define (`dt` in one column, `dd` in the other) stays intact.
 *
 * `children` is for the rare row whose value is not plain text — the support-champion contact link,
 * for instance. `value` still decides whether the row appears at all, so the caller cannot smuggle a
 * row past the empty check by passing children with a null value.
 */
export function Field({
  label,
  value,
  children,
}: {
  label: string;
  value: string | null | undefined;
  children?: React.ReactNode;
}) {
  if (value == null || value.trim() === '') return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>{children ?? value}</dd>
    </>
  );
}

// ── Team and RoleLabel: the community labels, and why they are not ordinary rows (PR-F50) ──────

/** One community-label row that has a value worth showing. `label` is the wording both pages use. */
export interface CommunityLabelRow {
  label: string;
  value: string;
}

/**
 * Which of `Team` / `RoleLabel` there is anything to show, in the order `PR-F50` names them.
 *
 * SEPARATE FROM THE COMPONENT so the one decision with a requirement attached can be asserted without
 * a DOM: `UR-F36` / `PR-F50` say "where neither is set, show nothing", and an empty array is how that
 * becomes true for the caller — no note, no heading, no empty group. WHITESPACE COUNTS AS EMPTY here for
 * the same reason it does in `Field` above: a team of `" "` is not an assignment, and a row holding a
 * space is exactly the visible hole `UR-F04` closes.
 *
 * The VALUE is passed through untrimmed, as `Field` passes its own through — trimming for display would
 * make this helper the one place in the profile pages that silently rewrites stored text, and the 40-
 * character cap that governs these two fields is a database CHECK (`0002`), not a client concern.
 */
export function communityLabelRows(
  team: string | null | undefined,
  roleLabel: string | null | undefined,
): CommunityLabelRow[] {
  const rows: CommunityLabelRow[] = [];
  if (typeof team === 'string' && team.trim() !== '') rows.push({ label: 'Team', value: team });
  if (typeof roleLabel === 'string' && roleLabel.trim() !== '') {
    rows.push({ label: 'Role label', value: roleLabel });
  }
  return rows;
}

/**
 * Scoped styles, injected by the component so it drops into either public-profile page unchanged — the
 * same reason `SupportChampionCard` carries its own. Both pages define their own `tb-userprofile__*` /
 * `tb-profile__*` vocabularies, so borrowing either would make this group render differently depending
 * on which page it sat on; the shared design tokens keep it matching both.
 *
 * WHY THE SELECTORS NAME BOTH CLASSES (`.tb-community-labels dl.tb-community-labels__rows dt`): each page
 * already styles `dt`/`dd` and the `dl` grid through a descendant rule of its own root class, which has
 * the SAME specificity as a single-class rule here — so the winner would be decided by which `<style>`
 * element happens to be later in the document, and this group's whole job is to look different from
 * those rows. Two classes deep settles it outright, whatever order the pages inject their styles in.
 */
const COMMUNITY_LABEL_STYLES = `
  .tb-community-labels {
    margin: 0.65rem 0 0; padding: 0.15rem 0 0.15rem 0.8rem;
    border-left: 2px solid var(--tb-line);
  }
  .tb-community-labels__note {
    color: var(--tb-muted); font-size: 0.75rem; margin: 0 0 0.3rem; max-width: 46ch;
  }
  .tb-community-labels dl.tb-community-labels__rows {
    display: grid; grid-template-columns: minmax(5.5rem, max-content) 1fr;
    gap: 0.2rem 0.9rem; margin: 0; font-size: 0.8125rem;
  }
  .tb-community-labels dl.tb-community-labels__rows dt { color: var(--tb-muted); font-weight: 600; }
  .tb-community-labels dl.tb-community-labels__rows dd { margin: 0; }
`;

/**
 * The person's `Team` and `RoleLabel` (`UR-F34`, `PR-F50`) — the working group they sit in and what they
 * do in the community — rendered as a group that is visibly SUBORDINATE to the directory rows above it.
 *
 * ── WHY THIS IS NOT TWO MORE `Field` ROWS ──────────────────────────────────────────────────────
 *
 * It was, on both pages, and that is the half of `PR-F50` that was still missing when `PR-F02` came to be
 * closed. The requirement is not merely "show them": it is "show them **next to, and visibly subordinate
 * to, the job title and department from the directory**, so a reader can tell which is the organisation's
 * record and which is a community label". Two more rows in the same `<dl>`, in the same weight and the
 * same colour as Job title and Department, satisfy the first clause and defeat the second — a reader sees
 * five equal facts and has no way to tell that three came from the corporate directory (`UR-F06`: only a
 * directory correction can change them) and two were typed by whoever assigns community labels.
 *
 * So the group is smaller, muted, indented behind a rule, and carries ONE line of prose saying where the
 * labels come from. The prose is the part that does the work: subordinate styling alone says "less
 * important", which is not the same statement as "a different source".
 *
 * ── WHAT THE WORDING MUST AND MUST NOT SAY ─────────────────────────────────────────────────────
 *
 * "Assigned by the organisation" — because `UR-F35`/`PR-F50` are explicit that the person **cannot edit
 * or hide either**, and a label sitting unattributed next to a self-declared block reads as one more
 * thing they chose for themselves. The same distinction `SupportChampionCard` draws in words for the
 * champion, for the same reason.
 *
 * NOT "self-declared" (they are not, and the About block's own note owns that phrase) and NOT anything
 * that reads as a rank, a level or a grade: a `RoleLabel` is "what they do in the community", never a
 * position in one (`PC-F31`). No figures here at all.
 *
 * RETURNS NOTHING when neither label is set (`UR-F36`, and `PR-F31`'s rule for every empty section) — no
 * note, no rule, no empty group. `communityLabelRows` above decides that, so the two pages cannot drift
 * apart on it, and a profile with no labels is indistinguishable from one on a build that never had them.
 */
export function CommunityLabels({
  team,
  roleLabel,
}: {
  team: string | null | undefined;
  roleLabel: string | null | undefined;
}) {
  const rows = communityLabelRows(team, roleLabel);
  if (rows.length === 0) return null;
  return (
    <div className="tb-community-labels">
      <style>{COMMUNITY_LABEL_STYLES}</style>
      <p className="tb-community-labels__note">
        In the community — assigned by the organisation, not part of the corporate directory record
        and not self-declared.
      </p>
      <dl className="tb-community-labels__rows">
        {rows.map((r) => (
          <Field key={r.label} label={r.label} value={r.value} />
        ))}
      </dl>
    </div>
  );
}
